import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  getNotificationTemplates,
  getNotificationTemplateDetail,
  createNotificationTemplate,
  updateNotificationTemplate,
  deleteNotificationTemplate
} from '@/service/orderTest'
import {
  getNotificationRules,
  createNotificationRule,
  updateNotificationRule,
  deleteNotificationRule,
  copyNotificationRule
} from '@/service/notification'

// ==================== 模拟数据 ====================

// 模拟消息模板数据
const mockTemplates = [
  {
    templateId: 1,
    templateName: '订单确认通知',
    templateType: 'order_confirm',
    title: '订单确认',
    content: '您的订单已确认，服务时间：{serviceTime}，服务人员：{employeeName}',
    channel: 'wechat',
    enabled: true,
    description: '订单确认时的通知模板',
    createdAt: '2024-01-15 10:00:00',
    updatedAt: '2024-01-15 10:00:00'
  },
  {
    templateId: 2,
    templateName: '订单提醒通知',
    templateType: 'order_reminder',
    title: '服务提醒',
    content: '您好，您的服务将在 {serviceTime} 开始，请提前做好准备。服务人员：{employeeName}',
    channel: 'wechat',
    enabled: true,
    description: '服务开始前的提醒通知',
    createdAt: '2024-01-16 09:00:00',
    updatedAt: '2024-01-16 09:00:00'
  },
  {
    templateId: 3,
    templateName: '派单通知',
    templateType: 'assign_notify',
    title: '新订单派单',
    content: '您有新的订单可以接单，订单号：{orderNo}，客户：{customerName}，服务时间：{serviceTime}',
    channel: 'app',
    enabled: true,
    description: '有新订单时的派单通知',
    createdAt: '2024-01-17 08:00:00',
    updatedAt: '2024-01-17 08:00:00'
  },
  {
    templateId: 4,
    templateName: '支付成功通知',
    templateType: 'payment_notify',
    title: '支付成功',
    content: '您的订单支付成功，订单号：{orderNo}，支付金额：{amount}元，感谢您的支持！',
    channel: 'wechat',
    enabled: true,
    description: '支付成功时的通知模板',
    createdAt: '2024-01-18 11:00:00',
    updatedAt: '2024-01-18 11:00:00'
  },
  {
    templateId: 5,
    templateName: '支付失败通知',
    templateType: 'payment_notify',
    title: '支付失败',
    content: '您的订单支付失败，订单号：{orderNo}，请重新支付或联系客服。',
    channel: 'sms',
    enabled: true,
    description: '支付失败时的通知模板',
    createdAt: '2024-01-19 14:00:00',
    updatedAt: '2024-01-19 14:00:00'
  },
  {
    templateId: 6,
    templateName: '生日提醒通知',
    templateType: 'birthday_reminder',
    title: '生日祝福',
    content: '亲爱的 {customerName}，今天是您的生日，祝您生日快乐！我们为您准备了生日惊喜，详情请咨询客服。',
    channel: 'wechat',
    enabled: true,
    description: '客户生日时的提醒通知',
    createdAt: '2024-01-20 10:00:00',
    updatedAt: '2024-01-20 10:00:00'
  },
  {
    templateId: 7,
    templateName: '订单确认通知（短信）',
    templateType: 'order_confirm',
    title: '订单确认',
    content: '您的订单已确认，服务时间：{serviceTime}，服务人员：{employeeName}，如有疑问请联系客服。',
    channel: 'sms',
    enabled: false,
    description: '订单确认时的短信通知模板',
    createdAt: '2024-01-21 09:00:00',
    updatedAt: '2024-01-21 09:00:00'
  },
  {
    templateId: 8,
    templateName: '服务完成通知',
    templateType: 'order_reminder',
    title: '服务完成',
    content: '您的服务已完成，感谢您的信任！如有任何问题，请随时联系我们。',
    channel: 'wechat',
    enabled: true,
    description: '服务完成后的通知模板',
    createdAt: '2024-01-22 16:00:00',
    updatedAt: '2024-01-22 16:00:00'
  },
  {
    templateId: 9,
    templateName: 'APP推送-派单通知',
    templateType: 'assign_notify',
    title: '新订单待接',
    content: '您有新的订单待接，订单号：{orderNo}，客户：{customerName}，服务地址：{address}，请及时查看。',
    channel: 'app',
    enabled: true,
    description: 'APP推送的派单通知',
    createdAt: '2024-01-23 08:30:00',
    updatedAt: '2024-01-23 08:30:00'
  },
  {
    templateId: 10,
    templateName: '订单取消通知',
    templateType: 'order_confirm',
    title: '订单已取消',
    content: '您的订单已取消，订单号：{orderNo}。如有疑问，请联系客服。',
    channel: 'wechat',
    enabled: true,
    description: '订单取消时的通知模板',
    createdAt: '2024-01-24 10:00:00',
    updatedAt: '2024-01-24 10:00:00'
  }
]

/** 解析通知规则列表（兼容 body / body.data） */
function extractNotificationRuleList (payload) {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.data)) return payload.data
  if (Array.isArray(payload.records)) return payload.records
  return []
}

/** list 项 notifyCategory 与 NotifyCategory 枚举 id 对齐 */
function ruleNotifyCategoryKey (rule) {
  const v = rule.notifyCategory
  if (v === undefined || v === null || v === '') return '_unknown'
  return String(v)
}

function groupRulesByNotifyCategory (list) {
  const byNotifyCategory = {}
  list.forEach((rule) => {
    const k = ruleNotifyCategoryKey(rule)
    if (!byNotifyCategory[k]) byNotifyCategory[k] = []
    byNotifyCategory[k].push(rule)
  })
  return byNotifyCategory
}

// ==================== 初始状态 ====================

const initialState = {
  // 模板列表状态
  list: {
    fetching: false,
    error: null,
    query: {},
    data: {
      list: [],
      total: 0,
      page: 1,
      pageSize: 10
    }
  },
  // 模板详情状态
  detail: {
    fetching: false,
    error: null,
    data: null
  },
  // 创建模板操作
  create: {
    fetching: false,
    error: null
  },
  // 更新模板操作
  update: {
    fetching: false,
    error: null
  },
  // 删除模板操作
  delete: {
    fetching: false,
    error: null
  },
  /** GET /notification/rule；data.byNotifyCategory：按 notifyCategory → 规则列表（与 NotifyCategory.id 对齐） */
  notificationRules: {
    fetching: false,
    error: null,
    query: {},
    data: {
      list: [],
      byNotifyCategory: {}
    }
  },
  /** POST /notification/rule */
  createNotificationRule: {
    fetching: false,
    error: null
  },
  /** PUT /notification/rule/{id} */
  updateNotificationRule: {
    fetching: false,
    error: null
  },
  /** DELETE /notification/rule/{id} */
  deleteNotificationRule: {
    fetching: false,
    error: null
  },
  /** POST /notification/rule/{id}/copy */
  copyNotificationRule: {
    fetching: false,
    error: null
  }
}

// ==================== 异步Action ====================

/**
 * 异步获取消息模板列表的thunk action
 * @param {Object} query - 查询参数对象
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchNotificationTemplates = createAsyncThunk(
  'serviceNotification/fetchNotificationTemplates',
  async (query = {}, { rejectWithValue }) => {
    try {
      // TODO: 对接真实API
      // const response = await getNotificationTemplates(query)
      // return {
      //   data: response.data,
      //   query
      // }
      
      // 模拟API响应
      return new Promise((resolve) => {
        setTimeout(() => {
          let filteredTemplates = [...mockTemplates]
          
          // 根据模板名称筛选
          if (query.templateName) {
            const keyword = query.templateName.toLowerCase()
            filteredTemplates = filteredTemplates.filter(template => 
              template.templateName.toLowerCase().includes(keyword)
            )
          }
          
          // 根据模板类型筛选
          if (query.templateType) {
            filteredTemplates = filteredTemplates.filter(template => 
              template.templateType === query.templateType
            )
          }
          
          // 根据通知渠道筛选
          if (query.channel) {
            filteredTemplates = filteredTemplates.filter(template => 
              template.channel === query.channel
            )
          }
          
          // 根据启用状态筛选
          if (query.enabled !== undefined && query.enabled !== '') {
            const enabled = query.enabled === 'true' || query.enabled === true
            filteredTemplates = filteredTemplates.filter(template => 
              template.enabled === enabled
            )
          }
          
          // 分页处理
          const page = query.page || 1
          const pageSize = query.pageSize || 10
          const start = (page - 1) * pageSize
          const end = start + pageSize
          const paginatedTemplates = filteredTemplates.slice(start, end)
          
          resolve({
            data: {
              list: paginatedTemplates,
              total: filteredTemplates.length,
              page: page,
              pageSize: pageSize
            },
            query
          })
        }, 300) // 模拟网络延迟
      })
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取消息模板列表失败')
    }
  }
)

/**
 * GET /notification/rule；落库后为 list + 按 notifyCategory 分组的 byNotifyCategory
 */
export const fetchNotificationRules = createAsyncThunk(
  'serviceNotification/fetchNotificationRules',
  async ({ query = {} } = {}, { rejectWithValue }) => {
    try {
      const response = await getNotificationRules(query)
      return {
        data: response.data,
        query
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取通知规则失败')
    }
  }
)

/**
 * POST /notification/rule
 */
export const createNotificationRuleAction = createAsyncThunk(
  'serviceNotification/createNotificationRule',
  async (body, { rejectWithValue }) => {
    try {
      const response = await createNotificationRule(body)
      return { data: response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data || '创建通知规则失败')
    }
  }
)

/**
 * PUT /notification/rule/{id}
 */
export const updateNotificationRuleAction = createAsyncThunk(
  'serviceNotification/updateNotificationRule',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      console.log('redux', body)
      const response = await updateNotificationRule(id, body)
      return { data: response.data, id }
    } catch (error) {
      return rejectWithValue(error.response?.data || '更新通知规则失败')
    }
  }
)

/**
 * DELETE /notification/rule/{id}
 */
export const deleteNotificationRuleAction = createAsyncThunk(
  'serviceNotification/deleteNotificationRule',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await deleteNotificationRule(id)
      return { data: response.data, id }
    } catch (error) {
      return rejectWithValue(error.response?.data || '删除通知规则失败')
    }
  }
)

/**
 * POST /notification/rule/{id}/copy
 */
export const copyNotificationRuleAction = createAsyncThunk(
  'serviceNotification/copyNotificationRule',
  async ({ id, targetStoreIds, overwrite }, { rejectWithValue }) => {
    try {
      const response = await copyNotificationRule(id, { targetStoreIds, overwrite })
      return { data: response.data, id }
    } catch (error) {
      return rejectWithValue(error.response?.data || '拷贝通知规则失败')
    }
  }
)

/**
 * 异步获取消息模板详情的thunk action
 * @param {string|number} templateId - 模板ID
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchNotificationTemplateDetail = createAsyncThunk(
  'serviceNotification/fetchNotificationTemplateDetail',
  async (templateId, { rejectWithValue }) => {
    try {
      // TODO: 对接真实API
      // const response = await getNotificationTemplateDetail(templateId)
      // return {
      //   data: response.data
      // }
      
      // 模拟API响应
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const template = mockTemplates.find(t => String(t.templateId) === String(templateId))
          if (template) {
            resolve({
              data: { ...template }
            })
          } else {
            reject({ response: { data: '模板不存在' } })
          }
        }, 200)
      })
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取消息模板详情失败')
    }
  }
)

/**
 * 异步创建消息模板的thunk action
 * @param {Object} data - 模板数据
 * @returns {Promise} 返回处理后的API响应数据
 */
export const createNotificationTemplateAction = createAsyncThunk(
  'serviceNotification/createNotificationTemplate',
  async (data, { rejectWithValue }) => {
    try {
      // TODO: 对接真实API
      // const response = await createNotificationTemplate(data)
      // return {
      //   data: response.data
      // }
      
      // 模拟API响应
      return new Promise((resolve) => {
        setTimeout(() => {
          const newTemplate = {
            templateId: mockTemplates.length > 0 ? Math.max(...mockTemplates.map(t => t.templateId)) + 1 : 1,
            ...data,
            createdAt: new Date().toLocaleString('zh-CN', { 
              year: 'numeric', 
              month: '2-digit', 
              day: '2-digit', 
              hour: '2-digit', 
              minute: '2-digit', 
              second: '2-digit',
              hour12: false
            }).replace(/\//g, '-'),
            updatedAt: new Date().toLocaleString('zh-CN', { 
              year: 'numeric', 
              month: '2-digit', 
              day: '2-digit', 
              hour: '2-digit', 
              minute: '2-digit', 
              second: '2-digit',
              hour12: false
            }).replace(/\//g, '-')
          }
          mockTemplates.push(newTemplate)
          resolve({
            data: newTemplate
          })
        }, 300)
      })
    } catch (error) {
      return rejectWithValue(error.response?.data || '创建消息模板失败')
    }
  }
)

/**
 * 异步更新消息模板的thunk action
 * @param {Object} params - 参数对象
 * @param {string|number} params.templateId - 模板ID
 * @param {Object} params.data - 更新数据
 * @returns {Promise} 返回处理后的API响应数据
 */
export const updateNotificationTemplateAction = createAsyncThunk(
  'serviceNotification/updateNotificationTemplate',
  async ({ templateId, data }, { rejectWithValue }) => {
    try {
      // TODO: 对接真实API
      // const response = await updateNotificationTemplate(templateId, data)
      // return {
      //   data: response.data,
      //   templateId
      // }
      
      // 模拟API响应
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const templateIndex = mockTemplates.findIndex(t => String(t.templateId) === String(templateId))
          if (templateIndex !== -1) {
            mockTemplates[templateIndex] = {
              ...mockTemplates[templateIndex],
              ...data,
              updatedAt: new Date().toLocaleString('zh-CN', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit', 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit',
                hour12: false
              }).replace(/\//g, '-')
            }
            resolve({
              data: mockTemplates[templateIndex],
              templateId
            })
          } else {
            reject({ response: { data: '模板不存在' } })
          }
        }, 300)
      })
    } catch (error) {
      return rejectWithValue(error.response?.data || '更新消息模板失败')
    }
  }
)

/**
 * 异步删除消息模板的thunk action
 * @param {string|number} templateId - 模板ID
 * @returns {Promise} 返回处理后的API响应数据
 */
export const deleteNotificationTemplateAction = createAsyncThunk(
  'serviceNotification/deleteNotificationTemplate',
  async (templateId, { rejectWithValue }) => {
    try {
      // TODO: 对接真实API
      // const response = await deleteNotificationTemplate(templateId)
      // return {
      //   data: response.data,
      //   templateId
      // }
      
      // 模拟API响应（软删除，标记为已删除状态）
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const templateIndex = mockTemplates.findIndex(t => String(t.templateId) === String(templateId))
          if (templateIndex !== -1) {
            // 软删除：从列表中移除
            mockTemplates.splice(templateIndex, 1)
            resolve({
              data: { success: true, message: '删除成功' },
              templateId
            })
          } else {
            reject({ response: { data: '模板不存在' } })
          }
        }, 300)
      })
    } catch (error) {
      return rejectWithValue(error.response?.data || '删除消息模板失败')
    }
  }
)

// ==================== Redux Slice ====================

const serviceNotificationSlice = createSlice({
  name: 'serviceNotification',
  initialState,
  reducers: {
    /**
     * 重置模板列表状态
     */
    resetTemplateListState: (state) => {
      state.list = {
        fetching: false,
        error: null,
        query: {},
        data: {
          list: [],
          total: 0,
          page: 1,
          pageSize: 10
        }
      }
    },
    /**
     * 重置模板详情状态
     */
    resetTemplateDetailState: (state) => {
      state.detail = {
        fetching: false,
        error: null,
        data: null
      }
    },
    /**
     * 重置创建模板操作
     */
    resetCreateState: (state) => {
      state.create = {
        fetching: false,
        error: null
      }
    },
    /**
     * 重置更新模板操作
     */
    resetUpdateState: (state) => {
      state.update = {
        fetching: false,
        error: null
      }
    },
    /**
     * 重置删除模板操作
     */
    resetDeleteState: (state) => {
      state.delete = {
        fetching: false,
        error: null
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // ========== 获取模板列表 ==========
      .addCase(fetchNotificationTemplates.pending, (state) => {
        state.list.fetching = true
        state.list.error = null
      })
      .addCase(fetchNotificationTemplates.fulfilled, (state, action) => {
        state.list.fetching = false
        state.list.error = null
        state.list.query = action.payload.query || {}
        state.list.data = action.payload.data || {
          list: [],
          total: 0,
          page: 1,
          pageSize: 10
        }
      })
      .addCase(fetchNotificationTemplates.rejected, (state, action) => {
        state.list.fetching = false
        state.list.error = action.payload
        state.list.data = {
          list: [],
          total: 0,
          page: 1,
          pageSize: 10
        }
      })
      // ========== 通知规则列表 ==========
      .addCase(fetchNotificationRules.pending, (state) => {
        state.notificationRules.fetching = true
        state.notificationRules.error = null
      })
      .addCase(fetchNotificationRules.fulfilled, (state, action) => {
        state.notificationRules.fetching = false
        state.notificationRules.error = null
        state.notificationRules.query = action.payload.query || {}
        const list = extractNotificationRuleList(action.payload.data)
        state.notificationRules.data.list = list
        state.notificationRules.data.byNotifyCategory = groupRulesByNotifyCategory(list)
      })
      .addCase(fetchNotificationRules.rejected, (state, action) => {
        state.notificationRules.fetching = false
        state.notificationRules.error = action.payload
        state.notificationRules.data.list = []
        state.notificationRules.data.byNotifyCategory = {}
      })
      .addCase(createNotificationRuleAction.pending, (state) => {
        state.createNotificationRule.fetching = true
        state.createNotificationRule.error = null
      })
      .addCase(createNotificationRuleAction.fulfilled, (state) => {
        state.createNotificationRule.fetching = false
        state.createNotificationRule.error = null
      })
      .addCase(createNotificationRuleAction.rejected, (state, action) => {
        state.createNotificationRule.fetching = false
        state.createNotificationRule.error = action.payload
      })
      .addCase(updateNotificationRuleAction.pending, (state) => {
        state.updateNotificationRule.fetching = true
        state.updateNotificationRule.error = null
      })
      .addCase(updateNotificationRuleAction.fulfilled, (state) => {
        state.updateNotificationRule.fetching = false
        state.updateNotificationRule.error = null
      })
      .addCase(updateNotificationRuleAction.rejected, (state, action) => {
        state.updateNotificationRule.fetching = false
        state.updateNotificationRule.error = action.payload
      })
      .addCase(deleteNotificationRuleAction.pending, (state) => {
        state.deleteNotificationRule.fetching = true
        state.deleteNotificationRule.error = null
      })
      .addCase(deleteNotificationRuleAction.fulfilled, (state) => {
        state.deleteNotificationRule.fetching = false
        state.deleteNotificationRule.error = null
      })
      .addCase(deleteNotificationRuleAction.rejected, (state, action) => {
        state.deleteNotificationRule.fetching = false
        state.deleteNotificationRule.error = action.payload
      })
      .addCase(copyNotificationRuleAction.pending, (state) => {
        state.copyNotificationRule.fetching = true
        state.copyNotificationRule.error = null
      })
      .addCase(copyNotificationRuleAction.fulfilled, (state) => {
        state.copyNotificationRule.fetching = false
        state.copyNotificationRule.error = null
      })
      .addCase(copyNotificationRuleAction.rejected, (state, action) => {
        state.copyNotificationRule.fetching = false
        state.copyNotificationRule.error = action.payload
      })
      // ========== 获取模板详情 ==========
      .addCase(fetchNotificationTemplateDetail.pending, (state) => {
        state.detail.fetching = true
        state.detail.error = null
      })
      .addCase(fetchNotificationTemplateDetail.fulfilled, (state, action) => {
        state.detail.fetching = false
        state.detail.error = null
        state.detail.data = action.payload.data
      })
      .addCase(fetchNotificationTemplateDetail.rejected, (state, action) => {
        state.detail.fetching = false
        state.detail.error = action.payload
        state.detail.data = null
      })
      // ========== 创建模板 ==========
      .addCase(createNotificationTemplateAction.pending, (state) => {
        state.create.fetching = true
        state.create.error = null
      })
      .addCase(createNotificationTemplateAction.fulfilled, (state) => {
        state.create.fetching = false
        state.create.error = null
      })
      .addCase(createNotificationTemplateAction.rejected, (state, action) => {
        state.create.fetching = false
        state.create.error = action.payload
      })
      // ========== 更新模板 ==========
      .addCase(updateNotificationTemplateAction.pending, (state) => {
        state.update.fetching = true
        state.update.error = null
      })
      .addCase(updateNotificationTemplateAction.fulfilled, (state, action) => {
        state.update.fetching = false
        state.update.error = null
        // 如果列表中有该模板，更新其数据
        const templateId = action.payload.templateId
        const template = state.list.data.list.find(item => String(item.templateId) === String(templateId))
        if (template && action.payload.data) {
          Object.assign(template, action.payload.data)
        }
      })
      .addCase(updateNotificationTemplateAction.rejected, (state, action) => {
        state.update.fetching = false
        state.update.error = action.payload
      })
      // ========== 删除模板 ==========
      .addCase(deleteNotificationTemplateAction.pending, (state) => {
        state.delete.fetching = true
        state.delete.error = null
      })
      .addCase(deleteNotificationTemplateAction.fulfilled, (state, action) => {
        state.delete.fetching = false
        state.delete.error = null
        // 从列表中移除该模板
        const templateId = action.payload.templateId
        state.list.data.list = state.list.data.list.filter(item => String(item.templateId) !== String(templateId))
        state.list.data.total = Math.max(0, state.list.data.total - 1)
      })
      .addCase(deleteNotificationTemplateAction.rejected, (state, action) => {
        state.delete.fetching = false
        state.delete.error = action.payload
      })
  }
})

export const {
  resetTemplateListState,
  resetTemplateDetailState,
  resetCreateState,
  resetUpdateState,
  resetDeleteState
} = serviceNotificationSlice.actions

export default serviceNotificationSlice.reducer

